import { useCallback, useMemo, useRef, useState } from "react";

import {
  type Attempt,
  type SessionResult,
  SessionRecorder,
  recordAttempt,
} from "@nav-games/games-core";
import {
  cockedHat,
  fixQuality,
  type Bearing,
  type Mark,
} from "@nav-games/pilotage";

import { HelpButton, HelpPanel } from "../../ui/HelpPanel";
import { ThreePointFixFigure } from "../../ui/ThreePointFixFigure";

interface ThreePointFixProps {
  onNavigate: (to: string) => void;
}

const MODULE = "pilotage";
const GAME = "three-point-fix";
const CARDS_PER_SESSION = 8;
const CHOICES = ["excellent", "good", "acceptable", "poor"] as const;

interface Card {
  marks: readonly Mark[];
  bearings: readonly Bearing[];
  fix: ReturnType<typeof cockedHat>;
  quality: ReturnType<typeof fixQuality>;
}

const MARK_NAMES = [
  "Lighthouse",
  "Headland",
  "Church spire",
  "Cardinal mark",
  "Beacon",
  "Tower",
  "Chimney",
  "Water tank",
  "Mast",
];

function randInRange(lo: number, hi: number): number {
  return lo + Math.random() * (hi - lo);
}

function randInt(lo: number, hi: number): number {
  return lo + Math.floor(Math.random() * (hi - lo + 1));
}

function buildCard(): Card {
  // Pick an observer position somewhere near the UK south coast for
  // plausibility (the actual coordinates don't matter for the game).
  const obsLat = randInRange(50, 52);
  const obsLon = randInRange(-2, 1);

  // Place three marks at random bearings + ranges around the observer.
  const marks: Mark[] = [];
  const bearingsTrue: number[] = [];
  // Pick first mark bearing uniformly, then space the others by ~110°
  // ± 30° so the geometry is fair.
  const base = randInt(0, 359);
  const offsets = [0, 110 + randInt(-30, 30), 220 + randInt(-30, 30)];

  for (let i = 0; i < 3; i++) {
    const trueBearing = (base + offsets[i]!) % 360;
    const rangeNm = randInRange(0.8, 3.5);
    // Convert range + bearing into lat/lon offset (flat-earth).
    const dLatNm = Math.cos((trueBearing * Math.PI) / 180) * rangeNm;
    const dLonNm = Math.sin((trueBearing * Math.PI) / 180) * rangeNm;
    const cosLat = Math.cos((obsLat * Math.PI) / 180);
    const markLat = obsLat + dLatNm / 60;
    const markLon = obsLon + dLonNm / (60 * cosLat);
    marks.push({
      label: `${MARK_NAMES[(i + randInt(0, MARK_NAMES.length - 1)) % MARK_NAMES.length]} ${i + 1}`,
      latDeg: markLat,
      lonDeg: markLon,
    });
    bearingsTrue.push(trueBearing);
  }

  // Introduce a random bearing error per mark to drive cocked-hat
  // variety. Most fixes should be "good" or "acceptable"; one in five
  // should be "poor" so the student sees the full range.
  const errorBudgetDeg = Math.random() < 0.2 ? 6 : 2;
  const bearings: Bearing[] = marks.map((m, i) => ({
    mark: m,
    trueDeg:
      (bearingsTrue[i]! + (Math.random() - 0.5) * 2 * errorBudgetDeg + 360) %
      360,
  }));

  const fix = cockedHat(bearings);
  const quality = fixQuality(fix.meanSideNm);
  return { marks, bearings, fix, quality };
}

export function ThreePointFix({ onNavigate }: ThreePointFixProps) {
  const recorder = useMemo(
    () => new SessionRecorder({ module: MODULE, game: GAME, length: CARDS_PER_SESSION }),
    [],
  );

  const [index, setIndex] = useState(0);
  const [card, setCard] = useState<Card>(() => buildCard());
  const [shownAt, setShownAt] = useState(() => Date.now());
  const [chosen, setChosen] = useState<string | null>(null);
  const [result, setResult] = useState<SessionResult | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const sessionStartedAt = useRef(Date.now());

  const advance = useCallback(() => {
    if (index + 1 >= CARDS_PER_SESSION) {
      setResult(recorder.summarise());
      return;
    }
    setIndex((i) => i + 1);
    setCard(buildCard());
    setShownAt(Date.now());
    setChosen(null);
  }, [index, recorder]);

  const onChoose = useCallback(
    (choice: string) => {
      if (chosen !== null || result) return;
      const ok = choice === card.quality.rating;
      const attempt: Omit<Attempt, "module"> = {
        char: card.quality.rating,
        ok,
        ms: Date.now() - shownAt,
        ts: Date.now(),
      };
      recorder.log(attempt);
      recordAttempt({ ...attempt, module: MODULE });
      setChosen(choice);
    },
    [card, chosen, recorder, result, shownAt],
  );

  if (result) {
    return (
      <EndCard
        result={result}
        sessionStartMs={sessionStartedAt.current}
        onAgain={() => {
          setIndex(0);
          setCard(buildCard());
          setShownAt(Date.now());
          setChosen(null);
          setResult(null);
          sessionStartedAt.current = Date.now();
        }}
        onBack={() => onNavigate("/play/pilotage")}
      />
    );
  }

  const revealed = chosen !== null;

  return (
    <>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
        <HelpButton onOpen={() => setHelpOpen(true)} />
      </div>
      <HelpPanel section={helpOpen ? "pilotage" : null} onClose={() => setHelpOpen(false)} />

      <p className="small muted">
        Question {index + 1} of {CARDS_PER_SESSION} · Rate this three-point fix
      </p>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Fix quality?</h3>
        <p className="small muted" style={{ marginBottom: 8 }}>
          The cocked hat below was plotted from three bearings to
          charted marks. How would you rate this fix?
        </p>

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            padding: 8,
            marginBottom: 12,
            background: "var(--bg-elev)",
            border: "1px solid var(--border)",
            borderRadius: "var(--radius-sm)",
          }}
        >
          <ThreePointFixFigure
            marks={card.marks}
            bearings={card.bearings}
            fix={card.fix}
            size={360}
          />
        </div>

        <div
          style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}
        >
          {CHOICES.map((c) => {
            const isCorrect = c === card.quality.rating;
            const isChosen = c === chosen;
            return (
              <button
                key={c}
                className="secondary"
                disabled={revealed}
                onClick={() => onChoose(c)}
                style={{
                  fontSize: 15,
                  padding: "12px 6px",
                  fontWeight: 600,
                  textTransform: "capitalize",
                  background:
                    revealed && isCorrect
                      ? "rgba(44, 122, 59, 0.18)"
                      : revealed && isChosen
                        ? "rgba(184, 48, 42, 0.18)"
                        : undefined,
                }}
              >
                {c}
              </button>
            );
          })}
        </div>

        {revealed && (
          <div
            role="status"
            aria-live="polite"
            style={{
              marginTop: 12,
              padding: 12,
              borderRadius: 6,
              background:
                chosen === card.quality.rating
                  ? "rgba(44, 122, 59, 0.10)"
                  : "rgba(184, 48, 42, 0.10)",
            }}
          >
            <p style={{ margin: 0, fontWeight: 600, textTransform: "capitalize" }}>
              {chosen === card.quality.rating
                ? `✓ Correct — ${card.quality.label}.`
                : `✗ Not quite — ${card.quality.label}.`}
            </p>
            <p className="small" style={{ margin: "8px 0 0" }}>
              Cocked-hat mean side:{" "}
              <strong>{card.fix.meanSideNm.toFixed(3)} nm</strong>.
              Bands: &lt; 0.1 = excellent, &lt; 0.2 = good, &lt; 0.4 =
              acceptable, more = poor.
            </p>
            <button onClick={advance} style={{ marginTop: 12, width: "100%" }}>
              {index + 1 >= CARDS_PER_SESSION ? "See score →" : "Next →"}
            </button>
          </div>
        )}
      </div>

      <button className="secondary" onClick={() => onNavigate("/play/pilotage")}>
        ← Back to Pilotage
      </button>
    </>
  );
}

function EndCard({
  result,
  sessionStartMs,
  onAgain,
  onBack,
}: {
  result: SessionResult;
  sessionStartMs: number;
  onAgain: () => void;
  onBack: () => void;
}) {
  const elapsedS = Math.round((Date.now() - sessionStartMs) / 1000);
  return (
    <>
      <h2>Session complete</h2>
      <div className="card">
        <p>
          <strong>{result.correct}</strong> / {result.total} correct
          {result.total > 0 && (
            <> ({Math.round((result.correct / result.total) * 100)}%)</>
          )}{" "}
          in {elapsedS}s.
        </p>
      </div>
      <div className="row" style={{ gap: 8, flexDirection: "column", alignItems: "stretch" }}>
        <button onClick={onAgain}>Play another</button>
        <button className="secondary" onClick={onBack}>
          ← Back to Pilotage
        </button>
      </div>
    </>
  );
}
